'use client'

import useMulticityJourneyLabels from '@/src/hooks/srp/multicity/useMulticityJourneyLabels'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { createContext, useContext, useState, type ReactNode } from 'react'
import MulticityJourneySegmentTabs from './multicityJourneySegmentTabs'

/**
 * Per-section journey tab index — local UI state only (resets to 0 on unmount).
 * Filter selections per journey live in RTK filterData arrays.
 */
const MulticityFilterJourneyScopeContext = createContext<number | undefined>(undefined)

type MulticityFilterJourneyScopeProps = {
    children: ReactNode
}

/** Shared journey tabs for a filter section (e.g. departure + arrival timings). */
export const MulticityFilterJourneyScope = ({ children }: MulticityFilterJourneyScopeProps) => {
    const t = useTranslations('srpFilters')
    const [journeyIndex, setJourneyIndex] = useState(0)
    const { isMulticity, journeyCount } = useSearchResults()
    const journeys = useMulticityJourneyLabels()
    const showTabs = isMulticity && journeyCount > 1

    return (
        <MulticityFilterJourneyScopeContext.Provider value={journeyIndex}>
            {showTabs ? (
                <Box sx={{ px: 2, pt: 1, pb: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900', mb: 1.5 }}>{t('flightTimings')}</Typography>
                    <MulticityJourneySegmentTabs
                        journeys={journeys}
                        selectedIndex={journeyIndex}
                        onSelect={setJourneyIndex}
                    />
                </Box>
            ) : null}
            {children}
        </MulticityFilterJourneyScopeContext.Provider>
    )
}

/** Journey index from scoped tabs, or local section state when not inside a scope. */
export const useMulticitySectionJourneyIndex = (localJourneyIndex: number) => {
    const scopedIndex = useContext(MulticityFilterJourneyScopeContext)
    return scopedIndex ?? localJourneyIndex
}
